import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { BadgeCheck, Check, X } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import {
  fetchVerificationRequests,
  approveVerificationRequest,
  rejectVerificationRequest,
  type AdminVerificationRequest,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, RelativeTime } from '@/components/DataDisplay'
import { Tag } from '@/components/StatusBadge'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field, Textarea } from '@/components/ui/input'
import { useToast } from '@/providers/UiProviders'

/**
 * A6b — заявки на верификацию, поданные через бота «Grom Verification» (отдельного от «Grom
 * Support»). Заявки только на бота/канал/чат — верификация личного профиля ботом не принимается.
 * Одобрение/отклонение здесь решает только саму заявку; реальную галочку админ выдаёт отдельно на
 * странице пользователя/группы/бота (у этого экрана нет надёжного способа сам понять, какой
 * реальный объект скрывается за свободным текстом @username).
 */
export function VerificationRequestsScreen() {
  const t = useT()
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [action, setAction] = useState<{ req: AdminVerificationRequest; kind: 'approve' | 'reject' } | null>(null)

  const columns = useMemo<AdminColumn<AdminVerificationRequest>[]>(
    () => [
      {
        key: 'created_at',
        header: t.reports.createdAt,
        type: 'date',
        width: 140,
      },
      {
        key: 'requester_user_id',
        header: t.reports.reporter,
        type: 'text',
        width: 160,
        render: (r) => (
          <Link to={`/users/${r.requester_user_id}`} className="text-accent hover:underline">
            {r.requester_username ? `@${r.requester_username}` : `#${r.requester_user_id}`}
          </Link>
        ),
      },
      {
        key: 'target_type',
        header: t.reports.target,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <span className="text-sm">
            <Tag>{r.target_type}</Tag> @{r.target_username}
          </span>
        ),
      },
      { key: 'reason', header: t.reports.comment, type: 'text', minWidth: 200 },
      {
        key: 'status',
        header: t.reports.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'open', label: t.reports.statusOpen, status: 'review' },
          { value: 'approved', label: t.verification.statusApproved, status: 'active' },
          { value: 'rejected', label: t.verification.statusRejected, status: 'deleted' },
        ],
      },
    ],
    [t],
  )

  const rowActions = useMemo<RowAction<AdminVerificationRequest>[]>(
    () => [
      {
        id: 'approve',
        label: t.verification.approve,
        icon: <Check size={13} />,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.status === 'open',
        onSelect: (r) => setAction({ req: r, kind: 'approve' }),
      },
      {
        id: 'reject',
        label: t.verification.reject,
        icon: <X size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.status === 'open',
        onSelect: (r) => setAction({ req: r, kind: 'reject' }),
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchVerificationRequests, [])

  return (
    <>
      <PageHeader title={t.nav.verificationRequests} screenId="A6.02" verified />
      <DataTable<AdminVerificationRequest>
        key={reloadKey}
        screenId="A6.02"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.id}
        rowActions={rowActions}
        emptyState={{ icon: <BadgeCheck size={28} strokeWidth={1.5} />, title: t.verification.emptyTitle }}
        detailPanel={(r) => (
          <div>
            <InfoRow label={t.col.id}>
              <MonoValue value={r.id} />
            </InfoRow>
            <InfoRow label={t.reports.comment}>{r.reason || '—'}</InfoRow>
            <InfoRow label={t.reports.createdAt}>
              <RelativeTime value={r.created_at} />
            </InfoRow>
            <ProofLinksSection request={r} />
            {r.status !== 'open' && (
              <>
                <InfoRow label={t.reports.resolvedBy}>{r.resolved_by_admin ?? '—'}</InfoRow>
                <InfoRow label={t.reports.resolutionLabel}>{r.resolution || '—'}</InfoRow>
              </>
            )}
          </div>
        )}
      />

      {action && (
        <ResolveDialog
          req={action.req}
          kind={action.kind}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </>
  )
}

function ResolveDialog({
  req,
  kind,
  onClose,
  onDone,
}: {
  req: AdminVerificationRequest
  kind: 'approve' | 'reject'
  onClose: () => void
  onDone: () => void
}) {
  const t = useT()
  const { toast } = useToast()
  const [resolution, setResolution] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    setSubmitting(true)
    try {
      const fn = kind === 'approve' ? approveVerificationRequest : rejectVerificationRequest
      await fn(req.id, resolution.trim() || undefined)
      toast(t.users.actionDone, { tone: 'success' })
      onDone()
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={kind === 'approve' ? t.verification.approveTitle : t.verification.rejectTitle}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button
            variant={kind === 'reject' ? 'danger' : 'primary'}
            onClick={() => void submit()}
            disabled={submitting}
          >
            {kind === 'approve' ? t.verification.approve : t.verification.reject}
          </Button>
        </>
      }
    >
      <Field label={t.reports.resolutionLabel}>
        <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={3} />
      </Field>
    </Dialog>
  )
}

const PRESS_CATEGORY = 'Press'

function ProofLinksSection({ request }: { request: AdminVerificationRequest }) {
  const t = useT()
  const links = request.proof_links ?? []
  if (links.length === 0) {
    return <InfoRow label={t.verification.platformProofs}>{t.verification.noProofLinks}</InfoRow>
  }

  const platformLinks = links.filter((l) => l.category !== PRESS_CATEGORY)
  const pressLinks = links.filter((l) => l.category === PRESS_CATEGORY)

  return (
    <>
      {platformLinks.length > 0 && (
        <InfoRow label={t.verification.platformProofs}>
          <ProofLinkList links={platformLinks} />
        </InfoRow>
      )}
      {pressLinks.length > 0 && (
        <InfoRow label={t.verification.pressProofs}>
          <ProofLinkList links={pressLinks} />
        </InfoRow>
      )}
    </>
  )
}

function ProofLinkList({ links }: { links: { category: string; url: string }[] }) {
  return (
    <div className="flex flex-col gap-1">
      {links.map((link, i) => (
        <div key={i} className="text-sm">
          <Tag>{link.category}</Tag>{' '}
          <a href={link.url} target="_blank" rel="noreferrer" className="text-accent hover:underline break-all">
            {link.url}
          </a>
        </div>
      ))}
    </div>
  )
}
